import { useCallback, useRef } from "react";
import i18n from "i18next";
import type { CustomAppAccess } from "src/api/generated/erp/customApps/runtime/customAppAccess.ts";
import {
	useBeforeCloseCallbackForActiveDialog,
	useCloseCallbackForActiveDialog,
} from "src/components/common/dialogs/GenericDialogContext.ts";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import type { CustomAppHostBridge, CustomAppViewOverride } from "src/customApps/runtime/customAppHostBridge.ts";
import { DataDirtyStateChangeHandler } from "src/utils/dataDirtyStateChangeHandler.ts";
import { CustomAppFrame } from "./CustomAppFrame.tsx";

export interface CustomAppViewOverrideFrameProps {
	access: CustomAppAccess;
	customAppId: number;
	appName: string;
	viewOverride: CustomAppViewOverride;
	onRequestClose: () => void;
	onDataDirtyStateChanged?: DataDirtyStateChangeHandler;
}

/**
 * Shows a Custom App in place of an Aavo view, inside the dialog of that view. Closing the dialog asks the app about
 * unsaved changes first.
 */
export function CustomAppViewOverrideFrame({
	access,
	customAppId,
	appName,
	viewOverride,
	onRequestClose,
	onDataDirtyStateChanged,
}: CustomAppViewOverrideFrameProps) {
	const { userPermissions } = useGlobalInitData();
	const bridgeRef = useRef<CustomAppHostBridge | undefined>(undefined);
	const onBridge = useCallback((bridge: CustomAppHostBridge | undefined) => {
		bridgeRef.current = bridge;
	}, []);

	useBeforeCloseCallbackForActiveDialog(async () => {
		const decision = await bridgeRef.current?.beforeClose();
		const warning = decision?.unresponsive ? i18n.t("custom_app_close_unresponsive") : decision?.warning;
		onDataDirtyStateChanged?.({ isDirty: Boolean(warning), confirmCloseWhileDirtyMessage: warning });
	});
	useCloseCallbackForActiveDialog(async () => {
		await bridgeRef.current?.notifyClosed();
	});

	return (
		<CustomAppFrame
			{...access}
			viewerPermissions={userPermissions}
			customAppId={customAppId}
			title={appName}
			viewOverride={viewOverride}
			onRequestClose={onRequestClose}
			onBridge={onBridge}
		/>
	);
}
